import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DatabaseDetailDto, UpdateDatabaseInput } from '@cpm/shared';
import { DATABASE_ENGINE_INFO, type DatabaseEngine } from '@cpm/shared';
import {
  ArrowLeft,
  Database,
  ExternalLink,
  Link2,
  Pencil,
  Play,
  RefreshCw,
  Square,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { BackupsPanel } from '../components/backups-panels.js';
import { Modal } from '../components/Modal.js';
import { EnvironmentPanel, StoragePanel } from '../components/resource-tabs.js';
import { ResourceMetricsPanel } from '../components/metrics-panels.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Spinner,
  Tabs,
  statusTone,
} from '../components/ui.js';

function EditDatabaseForm({
  detail,
  onDone,
}: {
  detail: DatabaseDetailDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const live = detail.live;
  const [form, setForm] = useState({
    name: detail.resource.name,
    image: live?.image ?? '',
    isPublic: live?.isPublic ?? false,
    publicPort: live?.publicPort ? String(live.publicPort) : '',
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const update = useMutation({
    mutationFn: (input: UpdateDatabaseInput) =>
      api.patch(`/api/databases/${detail.resource.id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['databases', detail.resource.id] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({
          name: form.name,
          image: form.image || undefined,
          isPublic: form.isPublic,
          publicPort: form.isPublic && form.publicPort ? Number(form.publicPort) : undefined,
        });
      }}
    >
      <div>
        <Label htmlFor="cfg-name">Name</Label>
        <Input id="cfg-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="cfg-image">Image</Label>
        <Input
          id="cfg-image"
          value={form.image}
          onChange={(e) => set({ image: e.target.value })}
          placeholder="e.g. postgres:16-alpine"
        />
      </div>
      <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            disabled={!detail.features.database_external_access && !form.isPublic}
            checked={form.isPublic}
            onChange={(e) => set({ isPublic: e.target.checked })}
          />
          Expose on a public port
          {!detail.features.database_external_access && (
            <span className="text-xs font-normal text-amber-600 dark:text-amber-400">
              (not included in the plan)
            </span>
          )}
        </label>
        {form.isPublic && (
          <div>
            <Label htmlFor="cfg-port">Public port (1024–65535)</Label>
            <Input
              id="cfg-port"
              type="number"
              min={1024}
              max={65535}
              value={form.publicPort}
              onChange={(e) => set({ publicPort: e.target.value })}
            />
          </div>
        )}
      </div>
      {update.error != null && <ErrorNote error={update.error} />}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Image changes apply on the next restart. Credentials cannot be changed here — they were
        shown once at creation.
      </p>
      <div className="flex justify-end">
        <Button type="submit" loading={update.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

function OverviewTab({ detail }: { detail: DatabaseDetailDto }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [linkUuid, setLinkUuid] = useState('');
  const id = detail.resource.id;
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['databases', id] });

  const act = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart') =>
      api.post(`/api/databases/${id}/${action}`),
    onSuccess: invalidate,
  });
  const link = useMutation({
    mutationFn: () => api.post(`/api/databases/${id}/link`, { coolifyUuid: linkUuid }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/databases/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      void navigate(`/subscriptions/${detail.resource.subscriptionId}`);
    },
  });

  const canAct = detail.features.deployments && detail.resource.coolifyUuid !== null;
  const live = detail.live;
  const engine = (detail.resource.details?.engine ?? null) as DatabaseEngine | null;

  return (
    <div className="space-y-4">
      {(act.error ?? remove.error) != null && <ErrorNote error={act.error ?? remove.error} />}

      {!detail.resource.coolifyUuid && (
        <Card>
          <CardHeader
            title="Not linked to Coolify"
            description="The panel reserved this database but has no Coolify UUID for it — paste the UUID from Coolify to repair the link."
          />
          <div className="flex flex-wrap items-center gap-2 px-5 py-4">
            <Input
              className="max-w-sm"
              placeholder="Coolify database UUID"
              value={linkUuid}
              onChange={(e) => setLinkUuid(e.target.value)}
            />
            <Button loading={link.isPending} disabled={!linkUuid.trim()} onClick={() => link.mutate()}>
              <Link2 className="size-4" /> Link
            </Button>
            {link.error != null && <ErrorNote error={link.error} />}
          </div>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Status" />
          <div className="space-y-3 px-5 py-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 dark:text-slate-400">State</span>
              <Badge tone={statusTone(detail.resource.status)}>{detail.resource.status}</Badge>
            </div>
            {detail.liveError && (
              <p className="text-xs text-amber-600 dark:text-amber-400">{detail.liveError}</p>
            )}
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <Button variant="secondary" disabled={!canAct} loading={act.isPending} onClick={() => act.mutate('start')}>
                <Play className="size-4" /> Start
              </Button>
              <Button variant="secondary" disabled={!canAct} onClick={() => act.mutate('restart')}>
                <RefreshCw className="size-4" /> Restart
              </Button>
              <Button variant="secondary" disabled={!canAct} onClick={() => act.mutate('stop')}>
                <Square className="size-4" /> Stop
              </Button>
            </div>
            {!detail.features.deployments && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Lifecycle actions are disabled — the plan does not include deployments.
              </p>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Configuration"
            actions={
              detail.resource.coolifyUuid && (
                <Button variant="secondary" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-4" /> Edit
                </Button>
              )
            }
          />
          <dl className="grid gap-x-6 gap-y-1.5 px-5 py-4 text-sm sm:grid-cols-2">
            {(
              [
                ['Engine', engine ? DATABASE_ENGINE_INFO[engine]?.label ?? engine : live?.type],
                ['Image', live?.image],
                [
                  'External access',
                  live ? (live.isPublic ? `public :${live.publicPort ?? '?'}` : 'internal only') : null,
                ],
                ['Memory limit', live?.limitsMemory === '0' ? 'Unlimited' : live?.limitsMemory],
                ['CPU limit', live?.limitsCpus === '0' ? 'Unlimited' : live?.limitsCpus],
                ['Coolify UUID', detail.resource.coolifyUuid],
              ] as Array<[string, string | null | undefined]>
            )
              .filter(([, value]) => value != null && value !== '')
              .map(([label, value]) => (
                <div
                  key={label}
                  className="flex justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0 dark:border-slate-800"
                >
                  <dt className="shrink-0 text-slate-500 dark:text-slate-400">{label}</dt>
                  <dd className="break-all text-right font-medium">{value}</dd>
                </div>
              ))}
          </dl>
          <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            Credentials were shown once at creation and are not stored by the panel.
          </p>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Danger zone"
          description="Deletes the database in Coolify (including volumes), then releases the plan quota. Admins only."
        />
        <div className="px-5 py-4">
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Delete this database? It is torn down in Coolify (container and volumes — ALL DATA IS LOST) and removed from the ledger.',
                )
              ) {
                remove.mutate();
              }
            }}
          >
            <Trash2 className="size-4" /> Delete database
          </Button>
        </div>
      </Card>

      <Modal title="Edit configuration" open={editOpen} onClose={() => setEditOpen(false)}>
        <EditDatabaseForm detail={detail} onDone={() => setEditOpen(false)} />
      </Modal>
    </div>
  );
}

function LogsNotice({ detail }: { detail: DatabaseDetailDto }) {
  return (
    <Card>
      <CardHeader
        title="Runtime logs"
        description="The Coolify API does not expose runtime logs for databases — only application logs. Use Coolify's own UI to inspect them."
      />
      <div className="px-5 py-4">
        {detail.coolifyBaseUrl ? (
          <a
            href={detail.coolifyBaseUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            Open Coolify <ExternalLink className="size-4" />
          </a>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Coolify is not configured.
          </p>
        )}
      </div>
    </Card>
  );
}

export function DatabaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState('overview');

  const detail = useQuery({
    queryKey: ['databases', id],
    queryFn: () => api.get<DatabaseDetailDto>(`/api/databases/${id}`),
    enabled: Boolean(id),
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const data = detail.data;
  const linked = data.resource.coolifyUuid !== null;

  return (
    <div>
      <Link
        to={`/subscriptions/${data.subscription.id}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> {data.subscription.name}
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Database className="size-6 text-slate-400" />
            {data.resource.name}
            <Badge tone={statusTone(data.resource.status)}>{data.resource.status}</Badge>
          </span>
        }
        description={
          <>
            Database of{' '}
            <Link
              to={`/clients/${data.subscription.clientId}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {data.subscription.clientName}
            </Link>{' '}
            · subscription{' '}
            <Link
              to={`/subscriptions/${data.subscription.id}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {data.subscription.name}
            </Link>
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'metrics', label: 'Metrics', disabled: !linked },
          { key: 'environment', label: 'Environment', disabled: !linked },
          { key: 'storage', label: 'Storage', disabled: !linked },
          { key: 'backups', label: 'Backups', disabled: !linked },
          { key: 'logs', label: 'Logs' },
        ]}
      />

      {tab === 'overview' && <OverviewTab detail={data} />}
      {tab === 'metrics' && <ResourceMetricsPanel resourceId={data.resource.id} />}
      {tab === 'environment' && (
        <EnvironmentPanel
          apiBase={`/api/databases/${data.resource.id}`}
          queryKey={['databases', data.resource.id, 'envs']}
          enabled={linked}
          featureEnabled={data.features.environment_variables ?? false}
          description="Applied on the next restart"
        />
      )}
      {tab === 'storage' && (
        <StoragePanel
          apiBase={`/api/databases/${data.resource.id}`}
          queryKey={['databases', data.resource.id, 'storages']}
          enabled={linked}
        />
      )}
      {tab === 'backups' && <BackupsPanel resourceId={data.resource.id} enabled={linked} />}
      {tab === 'logs' && <LogsNotice detail={data} />}
    </div>
  );
}
